import React from 'react';
import { CircularProgress, Button } from '@mui/material';
import NavigationButton from './NavigationButton';
import type { ActionButton } from '../../../services/chatService';

interface DynamicActionButtonProps {
  button: ActionButton;
  isLoading?: boolean;
  onClick: (button: ActionButton) => void;
}

const DynamicActionButton: React.FC<DynamicActionButtonProps> = ({
  button,
  isLoading = false,
  onClick,
}) => {
  const handleClick = () => {
    onClick(button);
  };

  // Handle navigation buttons specially
  if (button.action === 'generate_specific_navigation_button' && button.data) {
    return (
      <NavigationButton
        displayText={button.data.displayText || button.label}
        navigationPath={button.data.navigationPath}
        icon={button.data.icon}
        disabled={isLoading}
        isLoading={isLoading}
        onClick={handleClick}
      />
    );
  }

  // Handle all other action buttons with standard styling
  return (
    <Button
      onClick={handleClick}
      disabled={isLoading}
      variant="outlined"
      size="small"
      startIcon={isLoading ? <CircularProgress size={14} /> : undefined}
      sx={{
        borderRadius: 3,
        textTransform: 'none',
        fontSize: '0.8rem',
        minHeight: 32,
        borderColor: (theme) =>
          theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.2)'
            : 'rgba(0,0,0,0.2)',
        color: (theme) =>
          theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.9)'
            : 'rgba(0,0,0,0.8)',
        '&:hover': {
          borderColor: (theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.4)'
              : 'rgba(0,0,0,0.4)',
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.1)'
              : 'rgba(0,0,0,0.05)',
        },
        '&:disabled': {
          borderColor: (theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.1)'
              : 'rgba(0,0,0,0.1)',
          color: (theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.3)'
              : 'rgba(0,0,0,0.3)',
        },
      }}
    >
      {isLoading ? 'Processing...' : button.label}
    </Button>
  );
};

export default DynamicActionButton;
